import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { compliance } from "@/lib/api";
import { Shell } from "@/components/Shell";
import {
  AlertCircle, Clock, TrendingUp, CheckCircle2, Plus,
  FileWarning, Sparkles, ArrowRight
} from "lucide-react";

const STATUS_ORDER = ["ABERTO", "EM_TRIAGEM", "EM_INVESTIGACAO", "MANIFESTACAO", "EM_ANALISE", "PARECER_EMITIDO", "DECISAO_TOMADA", "CONCLUIDO"];
const STATUS_LABEL = {
  ABERTO: "Aberto", EM_TRIAGEM: "Em Triagem", EM_INVESTIGACAO: "Em Investigação",
  MANIFESTACAO: "Manifestação", EM_ANALISE: "Em Análise", PARECER_EMITIDO: "Parecer Emitido",
  DECISAO_TOMADA: "Decisão", CONCLUIDO: "Concluído", ARQUIVADO: "Arquivado"
};

export default function ComplianceDashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    compliance.dashboard().then(r => setData(r.data)).catch(() => setData({}));
  }, []);

  const stats = [
    { label: "Total de processos", value: data?.total ?? "—", icon: FileWarning, color: "#B28F4B" },
    { label: "Abertos", value: data?.abertos ?? "—", icon: AlertCircle, color: "#60A5FA" },
    { label: "Em investigação", value: data?.em_investigacao ?? "—", icon: TrendingUp, color: "#FB923C" },
    { label: "Vencendo em 5d", value: data?.vencendo ?? "—", icon: Clock, color: "#F87171" },
    { label: "Concluídos", value: data?.concluidos ?? "—", icon: CheckCircle2, color: "#34D399" },
  ];

  return (
    <Shell>
      <div className="p-6 md:p-10 fade-up">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
            <div>
              <div className="text-[#B28F4B] text-[10px] font-bold tracking-[0.3em] uppercase mb-3">
                Módulo Compliance
              </div>
              <h1 className="font-heading text-4xl md:text-5xl font-light text-white mb-2">
                Governança <span className="text-[#B28F4B] italic font-serif">Operacional</span>
              </h1>
              <p className="text-[#A1A4C7] max-w-2xl">
                Gestão de processos disciplinares, evidências e decisões executivas.
              </p>
            </div>
            <Link
              to="/compliance/ocorrencias/nova"
              data-testid="btn-nova-ocorrencia"
              className="inline-flex items-center gap-2 bg-[#B28F4B] hover:bg-[#D4AF37] text-[#1A1D42] font-bold px-5 py-3 rounded-lg transition-colors"
            >
              <Plus size={17} /> Nova Ocorrência
            </Link>
          </div>

          {/* KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-10">
            {stats.map((s, i) => (
              <div key={i} className="soeasy-card p-5" style={{ animationDelay: `${i * 60}ms` }}>
                <div className="flex items-start justify-between mb-3">
                  <div
                    className="w-9 h-9 rounded-lg flex items-center justify-center"
                    style={{ background: `${s.color}18`, border: `1px solid ${s.color}30` }}
                  >
                    <s.icon size={17} style={{ color: s.color }} strokeWidth={1.7} />
                  </div>
                </div>
                <div className="font-heading text-3xl font-light text-white mb-1">{s.value}</div>
                <div className="text-[10px] text-[#A1A4C7] uppercase tracking-widest font-semibold">{s.label}</div>
              </div>
            ))}
          </div>

          {/* AI Insight */}
          <div className="soeasy-card p-6 md:p-7 mb-10 relative overflow-hidden" style={{ background: "linear-gradient(135deg, #1F2350 0%, #131532 100%)" }}>
            <div className="absolute top-0 right-0 w-48 h-48 bg-[#B28F4B]/10 blur-[80px] rounded-full pointer-events-none" />
            <div className="relative flex items-start gap-4">
              <div className="w-11 h-11 rounded-xl bg-[#B28F4B]/15 border border-[#B28F4B]/40 flex items-center justify-center flex-shrink-0">
                <Sparkles size={20} className="text-[#B28F4B]" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <div className="text-[10px] font-bold tracking-[0.3em] uppercase text-[#B28F4B]">Insight IA · Claude Sonnet 4.6</div>
                  <span className="w-1.5 h-1.5 rounded-full bg-[#B28F4B] pulse-dot" />
                </div>
                <p className="text-white text-base md:text-lg leading-relaxed max-w-3xl mb-2">
                  Sua operação tem <span className="text-[#B28F4B] font-bold">{data?.abertos ?? 0} processos abertos</span> e{" "}
                  <span className="text-[#F87171] font-bold">{data?.vencendo ?? 0} vencendo em 5 dias</span>. 
                  Recomendação: priorize triagem de casos "ABERTO" antes de novas análises.
                </p>
                <div className="text-[#A1A4C7] text-xs">
                  IA analisa fluxo em tempo real. Use botão <em>"Gerar Parecer"</em> nos processos para minutas assistidas.
                </div>
              </div>
            </div>
          </div>

          {/* Processos recentes */}
          <div className="mb-6 flex items-end justify-between">
            <div>
              <h2 className="font-heading text-2xl font-medium text-white mb-1">Processos ativos</h2>
              <div className="text-xs text-[#A1A4C7]">Ordenados por data de criação</div>
            </div>
            <Link to="/compliance/ocorrencias" className="text-[#B28F4B] hover:text-[#D4AF37] text-sm font-semibold flex items-center gap-1">
              Ver todos <ArrowRight size={14} />
            </Link>
          </div>

          <div className="grid gap-3">
            {(data?.recentes || []).length === 0 && (
              <div className="soeasy-card p-10 text-center">
                <FileWarning className="mx-auto mb-3 text-[#B28F4B]" size={40} strokeWidth={1.2} />
                <div className="text-white text-lg font-heading mb-1">Nenhum processo ainda</div>
                <div className="text-[#A1A4C7] text-sm">Crie uma nova ocorrência para começar</div>
              </div>
            )}
            {(data?.recentes || []).map(p => (
              <Link
                key={p.id}
                to={`/compliance/processos/${p.id}`}
                data-testid={`processo-card-${p.id}`}
                className="soeasy-card p-5 flex flex-col md:flex-row md:items-center gap-4 group"
              >
                <div className="font-mono text-[#B28F4B] text-sm font-bold shrink-0 md:w-40">
                  {p.protocolo}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-white font-semibold truncate group-hover:text-[#B28F4B] transition-colors">
                    {p.titulo}
                  </div>
                  <div className="text-xs text-[#A1A4C7] mt-1 line-clamp-1">{p.descricao}</div>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`status-pill status-${p.status}`}>{STATUS_LABEL[p.status] || p.status}</span>
                  <ArrowRight size={16} className="text-[#A1A4C7] group-hover:text-[#B28F4B] group-hover:translate-x-1 transition-all" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </Shell>
  );
}
